import { analytics } from '@src/utility/analytics';
import { startTransition } from 'react';

import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';

/**
 * PLAT-11877 — flips the transcript's redlines view, for both of its entry
 * points: the Redlines option of the view dropdown (clip preview, Creator
 * Studio) and the standalone Redline toggle (the T&S tab, which has no view
 * dropdown). Extracted from `Transcript.tsx` for its line budget.
 */
export interface RedlineView {
  isOn: boolean;
  /** A "Show Only" filter owns the view: inert, and says why. */
  blocked: boolean;
  /** Transcript redlines, or the T&S tab's replaced terms. */
  isRedlineSurface: boolean;
  onToggle: () => void;
}

export function useRedlineToggle({
  captureCursorYLock,
  setShowHistory,
  videoCreator,
  isOn,
  blocked,
  isRedlineSurface,
}: Omit<RedlineView, 'onToggle'> & {
  /** Locks the cursor's on-screen Y before the view flips. */
  captureCursorYLock: () => void;
  setShowHistory: (update: (current: boolean) => boolean) => void;
  videoCreator: Pick<ITranscriptVideoCreator, 'story' | 'currentVideo'>;
}): RedlineView {
  const onToggle = () => {
    // Lock the cursor's on-screen Y (clamped ≥3 line-heights from the top) so
    // the post-switch scroll lands it in place; if it isn't visible, no lock
    // is set and `_TranscriptionDiff`'s open-anchor placement applies.
    captureCursorYLock();
    startTransition(() => {
      analytics.track('transcript_toggle_redline', {
        storyId: videoCreator.story?.id,
        storyTitle: videoCreator.story?.title,
        videoId: videoCreator.currentVideo?.id,
        videoTitle: videoCreator.currentVideo?.title,
      });
      setShowHistory(cur => !cur);
    });
  };
  return { isOn, blocked, isRedlineSurface, onToggle };
}
